<template>
    <report
        :report="report"
        :versions="versions"
        :loading="loading"
        :refreshing="refreshing"
        @reload="reload"
        @load-version="load_version"
    >
        <div class="lam-graph-container">
            <div class="uk-grid uk-grid-small uk-grid-width-1-3">
                <div>
                    <ul class="uk-subnav uk-subnav-line">
                        <li :class="{'uk-active': !state.filter}"><a href @click.prevent="set_state_value('filter', '')">All</a></li>
                        <li v-if="has_filtered_projects('pic')" :class="{'uk-active': state.filter === 'pic'}"><a href @click.prevent="set_state_value('filter', 'pic')">PIC</a></li>
                        <li v-if="has_filtered_projects('pm')" :class="{'uk-active': state.filter === 'pm'}"><a href @click.prevent="set_state_value('filter', 'pm')">PM</a></li>
                        <li v-if="has_filtered_projects('d')" :class="{'uk-active': state.filter === 'd'}"><a href @click.prevent="set_state_value('filter', 'd')">D</a></li>
                        <li v-if="has_filtered_projects('starred')" :class="{'uk-active': state.filter === 'starred'}"><a href @click.prevent="set_state_value('filter', 'starred')"><open-icon :glyph="(state.filter === 'starred') ? 'star' : 'star-o'" /></a></li>
                    </ul>
                </div>
                <form @submit.prevent.stop class="uk-form uk-text-center">
                    <form-select-user
                        v-if="is_associate_principal || is_principal || is_db_admin_active || is_developer"
                        :id="`${report_id}-user-filter`"
                        name="user_id"
                        :value="state.user_id"
                        :editable="true"
                        :required="false"
                        :filter="designers_filter"
                        class="uk-width-1-5"
                        style="margin: 0 auto;"
                        @input="filter_by_user"
                    />
                </form>
                <div class="uk-text-right">
                    <div class="uk-button-group lam-graph-filter">
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.mode === 'absolute'}"
                            @click.prevent="set_state_value('mode', 'absolute')"
                        >
                            Absolute
                        </base-button>
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.mode === 'relative'}"
                            @click.prevent="set_state_value('mode', 'relative')"
                        >
                            Relative
                        </base-button>
                    </div>
                </div>
            </div>

            <div class="uk-grid uk-grid-small uk-margin-bottom lam-graph">
                <div class="uk-width-1-1" style="position: relative;">
                    <budgets-graph
                        ref="chart"
                        :chart-data="chart_data"
                        :y-max="ymax"
                        style="height: 600px;"
                        @click="route_to_record"
                    />
                    <div class="uk-button-group lam-graph-toggles">
                        <base-button
                            flavor="link"
                            size="mini"
                            @click.prevent="datasets_set_visibility(false)"
                        >
                            Hide All
                        </base-button>
                        <base-button
                            flavor="link"
                            size="mini"
                            @click.prevent="datasets_set_visibility(true)"
                        >
                            Show All
                        </base-button>
                    </div>
                </div>
            </div>
        </div>
    </report>
</template>

<script>
import { orderBy } from "lodash"

import is_report from "./IsReport"
import handles_projects from "./HandlesProjects"
import uses_color_palette from "../../mixins/UsesColorPalette"

import FormSelectUser from "../../ui/FormSelectUser"
import BaseButton from "../../ui/BaseButton"
import OpenIcon from "../../ui/OpenIcon"
import Report from "./Report.vue"
import BudgetsGraph from "./BudgetsGraph.js"

export default {
    mixins: [is_report, handles_projects, uses_color_palette],
    components: {
        FormSelectUser,
        BaseButton,
        OpenIcon,
        Report,
        BudgetsGraph
    },
    computed: {
        fields () {
            return ["ns::project;fieldset::graph,computed_fee,computed_spent,proposal_amount"]
        },
        chart_data () {
            const data = {
                projects: [],
                labels: [],
                datasets: [
                    {
                        label: "Fee Spent",
                        backgroundColor: "#00274F",
                        borderColor: "#00274F",
                        categoryPercentage: 0.9,
                        barPercentage: 0.8,
                        data: [],
                        project_ids: []
                    },
                    {
                        label: "Fee Remaining",
                        backgroundColor: this.color_pending.border,
                        borderColor: this.color_pending.border,
                        categoryPercentage: 0.9,
                        barPercentage: 0.8,
                        data: []
                    },
                    {
                        label: "Over Budget",
                        backgroundColor: this.color_active.background,
                        borderColor: this.color_active.border,
                        categoryPercentage: 0.9,
                        barPercentage: 0.8,
                        data: []
                    }
                ]
            }

            if(this.report) {
                orderBy(this.filtered_data, project => {
                    if(project.number) return project.sortable_number
                    return "000000" + this.nibnut_filter("nibnut.smart-truncate", [project.name, 8])
                }, ["asc"]).forEach(project => {
                    data.projects.push(project)
                    data.datasets[0].project_ids.push(project.id)

                    if(project.number) data.labels.push(project.number)
                    else data.labels.push(this.nibnut_filter("nibnut.smart-truncate", [project.name, 8]))

                    project.remaining = project.computed_fee ? (project.computed_fee - project.computed_spent) : project.computed_spent
                    if(this.state.mode === "relative") {
                        if(project.remaining <= 0) {
                            data.datasets[0].data.push((project.computed_fee / project.computed_spent) * 100)
                            data.datasets[1].data.push(0)
                            if(project.remaining === 0) data.datasets[2].data.push(100)
                            else data.datasets[2].data.push((Math.abs(project.remaining) / project.computed_spent) * 100)
                        } else {
                            data.datasets[0].data.push((project.computed_spent / project.computed_fee) * 100)
                            data.datasets[1].data.push((project.remaining / project.computed_fee) * 100)
                            data.datasets[2].data.push(0)
                        }
                    } else {
                        data.datasets[0].data.push(project.computed_spent)
                        data.datasets[1].data.push((project.remaining >= 0) ? project.remaining : 0)
                        data.datasets[2].data.push((project.remaining >= 0) ? 0 : Math.abs(project.remaining))
                    }
                })

                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                if(this.state.mode === "relative") this.ymax = 100
                else {
                    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                    this.ymax = 0
                    for(let loop = 0; loop < data.datasets[0].data.length; loop++) {
                        let period_total = 0
                        data.datasets.forEach(dataset => {
                            period_total += dataset.data[loop]
                        })
                        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                        this.ymax = Math.max(period_total, this.ymax)
                    }
                }
            }

            return data
        }
    },
    data () {
        return {
            report_id: "budgets"
        }
    }
}
</script>
